import React, { useState, useEffect, MutableRefObject } from "react";
import { useForm, Controller } from "react-hook-form";
import { cardBrandList, stateList, CAProvinces } from "../../../util/CommonLists";
import { PATTERNS } from "../../../util/patterns";
import * as services from "../../../store/service";
import { Notification } from "../../common/Notification";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import BillingSecurityNote from "../../../components/Registration/Billing/BillingSecurityNote";
import RenderIcons from "../../../Assets/css/_icons/partner_icon/RenderIcons";
import { validateCardExpiry } from "../../../util/common";
import { useTranslation } from 'react-i18next';
import PhoneInput from "react-phone-number-input";
import i18nService from "../../../util/Internationalization/i18n/i18n";
import { isValidPhoneNo } from "../../../util/phoneNumberValidationUtil";
import * as httpClient from "../../../util/httpClient";

interface IProps {
    closeDialog: any;
    dialogId: string;
    creditCard: any;
}

const IDFProtectionPlanBillingInfo: React.FunctionComponent<IProps> = (props) => {
    const fullName = useSelector((state: ApplicationState) => {
        if (state.loggedInUser && state.loggedInUser.userInfo) {
            return state.loggedInUser.userInfo.fullName;
        }
    });

    const [loading, setLoading] = useState(false);
    const [state, setState] = useState({ cardType: "" });
    const [enableBillingAddressForRecurly, setEnableBillingAddressForRecurly] = useState(false);
    const [addressState, setAddressState] = useState({
        address1: "",
        address2: "",
        city: "",
        state: {
            options: [],
            selection: "",
            readOnly: true,
        },
        zipCode: "",
        phone: "",
        country: "",
    });
    let phoneInputRef: MutableRefObject<HTMLInputElement> | null = null;
    const [zipCode, setZipCode] = useState(i18nService.get("ZipCode"));
    const partnerPhoneNumber: string = useSelector((state: ApplicationState) =>
        state.configurationState && state.configurationState.configuration.partnerConfig.phoneNumber
            ? state.configurationState.configuration.partnerConfig.phoneNumber
            : ""
    );
    const accountInfo = useSelector((state: ApplicationState) => (state.myAccountServiceState ? state.myAccountServiceState._unifiedAccountInfo : null));
    const { register, handleSubmit, errors, setValue, setError, clearErrors, control, watch } = useForm({
        mode: "onSubmit",
        reValidateMode: "onChange",
    });
    const { t: tLang } = useTranslation();

    const RefreshAndClose = () => {
        document.body.classList.remove("no_scroll");
        props.closeDialog();
    };

    const formatePhoneNumber = (phoneNumber: string) => {
        let phoneNumberWithoutMasking = phoneNumber;
        phoneNumberWithoutMasking = phoneNumberWithoutMasking?.replace(" ", "");
        phoneNumberWithoutMasking = phoneNumberWithoutMasking?.replace("(", "");
        phoneNumberWithoutMasking = phoneNumberWithoutMasking?.replace(")", "");
        phoneNumberWithoutMasking = phoneNumberWithoutMasking?.replace("-", "");
        return phoneNumberWithoutMasking;
    };

    const submitBillingInfo = (data: any) => {
        let expDate = data.expirationDate.split("/");
        const billingAddress = {
            Address1: data.address1,
            Address2: data.address2,
            City: data.city,
            State: {
                Selection: data.state,
            },
            ZipCode: data.zipcode,
            Phone: formatePhoneNumber(data.phone),
        };
        setLoading(true);
        services.SaveIDFPaymentInfo(data.cardholderName, data.cardNumber, data.cardType, expDate[0], expDate[1], data.zipcode, data.securityCode, data.expirationDate, billingAddress).then((data) => {
            setLoading(false);
            if (data.isSuccess) {
                props.closeDialog();
            } else if (data.errors && data.errors.length > 0) {
                Notification({
                    type: "error",
                    title: "Billing Information",
                    message: data.errors[0],
                    duration: 5,
                });
            } else {
                setLoading(false);
                Notification({
                    type: "error",
                    title: "Billing Information",
                    message: "Could not save information. Please try again later.",
                    duration: 5,
                });
            }
        });
    };

    let keyPressed: number;
    const keyCode = (e: any) => {
        keyPressed = e.keyCode;
    };
    const addMask = (e: any) => {
        e.preventDefault();
        if (e.target.value.length == 2 && keyPressed != 8 && keyPressed !== 46) {
            e.target.value = e.target.value + "/";
        }
    };

    useEffect(() => {
        httpClient.sendRequest("GET", `api/billing-info/get-enabled-billing-address-flag-for-recurly`).then((data: any) => {
            setEnableBillingAddressForRecurly(data);
        })
    }, []);

    useEffect(() => {
        document.querySelector<any>(`[aria-labelledby=${props.dialogId}-title]`)!.classList.add("width-lg");

        window.addEventListener("keyup", (evt: any) => {
            evt = evt || window.event;
            var isEscape = false;
            if ("key" in evt) {
                isEscape = evt.key === "Escape" || evt.key === "Esc";
            } else {
                isEscape = evt.keyCode === 27;
            }
            if (isEscape) {
                props.closeDialog();
            }
        });
        return () => {
            window.removeEventListener("keyup", () => {});
        };
    });

    const validateCreditCardNumber = {
        invalid: (val: any) => {
            if (state.cardType === "AMEX") {
                return /^$|^(\d{15})$/.test(val);
            }
            return /^$|^(\d{16})$/.test(val);
        },
    };

    const validateSecurityCode = {
        invalid: (val: any) => {
            if (state.cardType === "AMEX") {
                return /^$|^(\d{4})$/.test(val);
            }
            return /^$|^(\d{3})$/.test(val);
        },
    };

    const changeCardType = (event: any) => {
        setState({
            ...state,
            cardType: event.target.value,
        });
        setValue("cardNumber", "");
        setValue("securityCode", "");
    };

    const getCardType = () => {
        let cardType = props.creditCard?.cardType?.selection;
        const cardObjectWithKeyValue = cardBrandList.find((card) => card.value === cardType || card.name === cardType);
        if (cardObjectWithKeyValue) {
            cardType = cardObjectWithKeyValue.value;
        }
        return cardType;
    };

    const billingOperation = () => {
        let text = "Add ";
        if ([props.creditCard?.cardNumber, props.creditCard?.nameOnCard].join("")) {
            text = "Update ";
        }
        return text;
    };

    return (
        <React.Fragment>
            <div className="dialog-header">
                <div className="dialog-icon">
                    <RenderIcons iconName="coins" />
                </div>
                <h1>{billingOperation()} Billing Information</h1>
                <BillingSecurityNote />
            </div>
            <form id="protection-plan-billing-form-container" onSubmit={handleSubmit(submitBillingInfo)}>
                <div className={loading ? "loading dialog-body large-dialog-scroll" : "dialog-body large-dialog-scroll"}>
                    <div className="form-row">
                        <React.Fragment>
                            <fieldset className="form-row">
                                <div className="row">
                                    <div className="col-xs-12 col-sm-4 col-no-gutter">
                                        <legend className="legend-label">
                                            <label htmlFor="label-cardholder-name">
                                                Cardholder Name{" "}
                                                <span className="required" title="Mandatory">
                                                    *
                                                </span>
                                            </label>
                                        </legend>
                                    </div>
                                    <div className="col-xs-12 col-sm-8 col-no-gutter">
                                        <div className="row">
                                            <div className="col-xs-12 col-no-gutter">
                                                <div className={`form-row row ${errors["cardholderName"] && "status-error"}`}>
                                                    <div className="form-input col-xs-12 col-no-gutter">
                                                        <input
                                                            type="text"
                                                            id="label-cardholder-name"
                                                            className="inputbox"
                                                            name="cardholderName"
                                                            defaultValue={fullName}
                                                            ref={register({ required: true, pattern: PATTERNS.CARDHOLDERNAME.pattern, maxLength: 30 })}
                                                            placeholder="Cardholder Name"
                                                            aria-required="true"
                                                        />
                                                        {errors["cardholderName"] && errors["cardholderName"].type === "required" && (
                                                            <span className="form-error">Cardholder name is required.</span>
                                                        )}
                                                        {errors["cardholderName"] && errors["cardholderName"].type === "pattern" && (
                                                            <span className="form-error">{PATTERNS.CARDHOLDERNAME.message}</span>
                                                        )}
                                                        {errors["cardholderName"] && errors["cardholderName"].type === "maxLength" && (
                                                            <span className="form-error">Cardholder name must be less than 30 characters length.</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="form-row inline">
                                <div className="row">
                                    <div className="col-xs-12 col-sm-4 col-no-gutter">
                                        <legend className="legend-label">
                                            <label htmlFor="label-card-type">
                                                Card Type{" "}
                                                <span className="required" title="Mandatory">
                                                    *
                                                </span>
                                            </label>
                                        </legend>
                                    </div>
                                    <div className="col-xs-12 col-sm-8 col-no-gutter">
                                        <div className="row">
                                            <div className="col-xs-12 col-no-gutter">
                                                <div className={`form-row row ${errors["cardType"] && "status-error"}`}>
                                                    <div className="form-input col-xs-12 col-no-gutter">
                                                        <select
                                                            name="cardType"
                                                            className="inputbox"
                                                            id="label-card-type"
                                                            defaultValue={getCardType()}
                                                            ref={register({ required: true })}
                                                            onChange={changeCardType}
                                                            aria-required="true"
                                                        >
                                                            {cardBrandList.map((val: any) => (
                                                                <option key={val.value} value={val.value}>
                                                                    {val.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {errors["cardType"] && errors["cardType"].type === "required" && (
                                                            <span className="form-error">Card type is required.</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="form-row">
                                <div className="row">
                                    <div className="col-xs-12 col-sm-4 col-no-gutter">
                                        <legend className="legend-label">
                                            <label htmlFor="label-card-number">
                                                Card Number{" "}
                                                <span className="required" title="Mandatory">
                                                    *
                                                </span>
                                            </label>
                                        </legend>
                                    </div>
                                    <div className="col-xs-12 col-sm-8 col-no-gutter">
                                        <div className="row">
                                            <div className="col-xs-12 col-no-gutter">
                                                <div className={`form-row row ${errors["cardNumber"] && "status-error"}`}>
                                                    <div className="form-input col-xs-12 col-no-gutter">
                                                        <input
                                                            type="text"
                                                            id="label-card-number"
                                                            className="inputbox"
                                                            name="cardNumber"
                                                            ref={register({ required: true, validate: validateCreditCardNumber })}
                                                            maxLength={state.cardType === "AMEX" ? 15 : 16}
                                                            placeholder="Card Number"
                                                            aria-required="true"
                                                        />
                                                        {errors["cardNumber"] && errors["cardNumber"].type === "required" && (
                                                            <span className="form-error">Card Number is required.</span>
                                                        )}
                                                        {errors["cardNumber"] && errors["cardNumber"].type === "pattern" && (
                                                            <span className="form-error">A valid card number is required.</span>
                                                        )}
                                                        {errors["cardNumber"] && errors["cardNumber"].type === "invalid" && (
                                                            <span className="form-error">
                                                                A valid card number is required. Card number should be of {state.cardType === "AMEX" ? "15" : "16"}{" "}
                                                                digit
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="form-row">
                                <div className="row">
                                    <div className="col-xs-12 col-sm-4 col-no-gutter">
                                        <legend className="legend-label">
                                            <label htmlFor="expirationDate">
                                                Expiration Date{" "}
                                                <span className="required" title="Mandatory">
                                                    *
                                                </span>
                                            </label>
                                        </legend>
                                    </div>
                                    <div className="col-xs-12 col-sm-8 col-no-gutter">
                                        <div className="row">
                                            <div className="col-xs-12 col-no-gutter">
                                                <div className={`form-row row ${errors["expirationDate"] && "status-error"}`}>
                                                    <div className="form-input col-xs-12 col-no-gutter">
                                                        <input
                                                            name="expirationDate"
                                                            id="expirationDate"
                                                            type="text"
                                                            onKeyDown={keyCode}
                                                            onChange={addMask}
                                                            className="inputbox"
                                                            ref={register({
                                                                required: true,
                                                                maxLength: 5,
                                                                pattern: /(0[1-9]|1[012])[/]([0-9][1-9]|[1-9][0-9])/,
                                                                validate: (value: string) => {
                                                                    if (value.length === 5) {
                                                                        const expireDate = value.split("/");
                                                                        return validateCardExpiry(expireDate[0], expireDate[1]);
                                                                    }
                                                                    return true;
                                                                },
                                                            })}
                                                            maxLength={5}
                                                            placeholder="MM/YY"
                                                            aria-required="true"
                                                        />
                                                        {errors["expirationDate"] && errors["expirationDate"].type === "required" && (
                                                            <span className="form-error">Expiration Date is required.</span>
                                                        )}
                                                        {errors["expirationDate"] && errors["expirationDate"].type === "pattern" && (
                                                            <span className="form-error">Expiration date must contain a valid 2-digit month and 4-digit year.</span>
                                                        )}
                                                        {errors["expirationDate"] && errors["expirationDate"].type === "validate" && (
                                                            <span className="form-error">{`Invalid expiry date. If you continue to have this issue please call ${partnerPhoneNumber}.`}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="form-row">
                                <div className="row">
                                    <div className="col-xs-12 col-sm-4 col-no-gutter">
                                        <legend className="legend-label">
                                            <label htmlFor="label-security-code">
                                                Security Code{" "}
                                                <span className="required" title="Mandatory">
                                                    *
                                                </span>
                                            </label>
                                        </legend>
                                    </div>
                                    <div className="col-xs-12 col-sm-8 col-no-gutter">
                                        <div className="row">
                                            <div className="col-xs-12 col-no-gutter">
                                                <div className={`form-row row ${errors["securityCode"] && "status-error"}`}>
                                                    <div className="form-input col-xs-12 col-no-gutter">
                                                        <input
                                                            type="password"
                                                            id="label-security-code"
                                                            className="inputbox"
                                                            name="securityCode"
                                                            ref={register({ required: true, validate: validateSecurityCode })}
                                                            maxLength={state.cardType === "AMEX" ? 4 : 3}
                                                            placeholder="CVV"
                                                            aria-required="true"
                                                        />
                                                        {errors["securityCode"] && errors["securityCode"].type === "required" && (
                                                            <span className="form-error">CVV is required.</span>
                                                        )}
                                                        {errors["securityCode"] && errors["securityCode"].type === "invalid" && (
                                                            <span className="form-error">
                                                                Invalid CVV. CVV should be of {state.cardType === "AMEX" ? "4" : "3"} digit
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </fieldset>

                            {!enableBillingAddressForRecurly && (
                                <fieldset className="form-row inline">
                                    <div className="row">
                                        <div className="col-xs-12 col-sm-4 col-no-gutter">
                                            <legend className="legend-label">
                                                <label htmlFor="label-address-zipcode">
                                                    Zip Code{" "}
                                                    <span className="required" title="Mandatory">
                                                        *
                                                    </span>
                                                </label>
                                            </legend>
                                        </div>

                                        <div className="col-xs-12 col-sm-8 col-no-gutter">
                                            <div className="row">
                                                <div className="col-xs-12 col-no-gutter">
                                                    <div className={`form-row row ${errors["zipcode"] && "status-error"}`}>
                                                        <div className="form-input col-xs-12 col-no-gutter">
                                                            <input
                                                                type="text"
                                                                id="label-address-zipcode"
                                                                className="inputbox"
                                                                name="zipcode"
                                                                defaultValue={props.creditCard?.billingZip}
                                                                ref={register({ required: true, pattern: PATTERNS.IDF_BILLING_PROFILE_ZIPCODE.pattern })}
                                                                placeholder="Zip Code"
                                                                aria-required="true"
                                                            />
                                                            {errors["zipcode"] && errors["zipcode"].type === "required" && (
                                                                <span className="form-error">Zip code is required.</span>
                                                            )}
                                                            {errors["zipcode"] && errors["zipcode"].type === "pattern" && (
                                                                <span className="form-error">{PATTERNS.IDF_BILLING_PROFILE_ZIPCODE.message}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </fieldset>
                            ) }
                        </React.Fragment>
                    </div>
                    {enableBillingAddressForRecurly && (
                        <>
                            <div className="line-divider margin-vertical-l"></div>
                            <div className="form-row">
                                <div className="dialog-icon">
                                    <h3>
                                        <RenderIcons iconName="map-marker" /> {tLang("manageAccount.protectionPlan.billingInformation.label.billingAddress")}
                                    </h3>
                                </div>
                                <div className="margin-vertical-xs"></div>
                                <React.Fragment>
                                    <fieldset className="form-row inline">
                                        <div className="row">
                                            <div className="col-xs-12 col-sm-4 col-no-gutter">
                                                <legend className="legend-label">
                                                    <label htmlFor="label-address-line-1">
                                                        {tLang("manageAccount.protectionPlan.billingInformation.label.street")}{" "}
                                                        <span className="required" title="Mandatory">
                                                            *
                                                        </span>
                                                    </label>
                                                </legend>
                                            </div>

                                            <div className="col-xs-12 col-sm-8 col-no-gutter">
                                                <div className={`form-row row ${errors["address1"] && "status-error"}`}>
                                                    <div className="form-label sr-only col-xs-12 col-no-gutter">
                                                        <label htmlFor="label-address-line1">
                                                            {tLang("manageAccount.protectionPlan.billingInformation.label.addressLine1")}
                                                        </label>
                                                    </div>
                                                    <div className="form-input col-xs-12 col-no-gutter">
                                                        <input
                                                            type="text"
                                                            name="address1"
                                                            className="inputbox"
                                                            defaultValue={addressState.address1}
                                                            onChange={(e) => {
                                                                setAddressState({ ...addressState, address1: e.target.value });
                                                                clearErrors("billingAddress");
                                                            }}
                                                            id="label-address-line-1"
                                                            ref={register({
                                                                required: true,
                                                                pattern: PATTERNS.STREETADDRESS.pattern,
                                                            })}
                                                            placeholder={tLang("manageAccount.protectionPlan.billingInformation.placeholder.addressLine1")}
                                                            aria-required="true"
                                                        />
                                                        {errors["address1"] && errors["address1"].type === "required" && (
                                                            <span className="form-error">
                                                                {tLang("manageAccount.protectionPlan.billingInformation.validation.address1.required")}
                                                            </span>
                                                        )}
                                                        {errors["address1"] && errors["address1"].type === "pattern" && (
                                                            <span className="form-error">
                                                                {tLang("manageAccount.protectionPlan.billingInformation.validation.address1.pattern")}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className={`form-row row ${errors["address2"] && "status-error"}`}>
                                                    <div className="form-label sr-only col-xs-12 col-no-gutter">
                                                        <label htmlFor="label-address-line-2">
                                                            {tLang("manageAccount.protectionPlan.billingInformation.label.addressLine2")}
                                                        </label>
                                                    </div>
                                                    <div className="form-input col-xs-12 col-no-gutter">
                                                        <input
                                                            type="text"
                                                            name="address2"
                                                            className="inputbox"
                                                            defaultValue={addressState.address2}
                                                            onChange={(e) => {
                                                                setAddressState({ ...addressState, address2: e.target.value });
                                                                clearErrors("billingAddress");
                                                            }}
                                                            id="label-address-line-2"
                                                            ref={register({
                                                                pattern: PATTERNS.STREETADDRESS.pattern,
                                                            })}
                                                            placeholder={tLang("manageAccount.protectionPlan.billingInformation.placeholder.addressLine2")}
                                                        />
                                                        {errors["address2"] && errors["address2"].type === "pattern" && (
                                                            <span className="form-error">
                                                                {tLang("manageAccount.protectionPlan.billingInformation.validation.address2.pattern")}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </fieldset>
                                    <fieldset className="form-row inline">
                                        <div className="row">
                                            <div className="col-xs-12 col-sm-4 col-no-gutter">
                                                <legend className="legend-label">
                                                    {" "}
                                                    <label htmlFor="label-address-city">
                                                        {tLang("manageAccount.protectionPlan.billingInformation.label.city")}{" "}
                                                        <span className="required" title="Mandatory">
                                                            *
                                                        </span>
                                                    </label>
                                                </legend>
                                            </div>

                                            <div className="col-xs-12 col-sm-8 col-no-gutter">
                                                <div className="row">
                                                    <div className="col-xs-12 col-no-gutter">
                                                        <div className={`form-row row ${errors["city"] && "status-error"}`}>
                                                            <div className="form-input col-xs-12 col-no-gutter">
                                                                <input
                                                                    type="text"
                                                                    name="city"
                                                                    className="inputbox"
                                                                    defaultValue={addressState.city}
                                                                    onChange={(e) => {
                                                                        setAddressState({ ...addressState, city: e.target.value });
                                                                        clearErrors("billingAddress");
                                                                    }}
                                                                    id="label-address-city"
                                                                    ref={register({ required: true, pattern: PATTERNS.CITY.pattern })}
                                                                    placeholder={tLang("manageAccount.protectionPlan.billingInformation.placeholder.city")}
                                                                    aria-required="true"
                                                                />
                                                                {errors["city"] && errors["city"].type === "required" && (
                                                                    <span className="form-error">
                                                                        {tLang("manageAccount.protectionPlan.billingInformation.validation.city.required")}
                                                                    </span>
                                                                )}
                                                                {errors["city"] && errors["city"].type === "pattern" && (
                                                                    <span className="form-error">
                                                                        {tLang("manageAccount.protectionPlan.billingInformation.validation.city.pattern")}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </fieldset>
                                    <fieldset className="form-row inline">

                                        {accountInfo?.country !== null && accountInfo?.country === "CA" ? (
                                            <div className="row">
                                                <div className="col-xs-12 col-sm-4 col-no-gutter">
                                                    <legend className="legend-label">
                                                        {" "}
                                                        <label htmlFor="label-address-state">
                                                            Province{" "}
                                                            <span className="required" title="Mandatory">
                                                                *
                                                            </span>
                                                        </label>
                                                    </legend>
                                                </div>

                                                <div className="col-xs-12 col-sm-8 col-no-gutter">
                                                    <div className="row">
                                                        <div className="col-xs-12 col-no-gutter">
                                                            <div className={`form-row row ${errors["state"] && "status-error"}`}>
                                                                <div className="form-input col-xs-12 col-no-gutter">
                                                                    <select
                                                                        name="state"
                                                                        className="inputbox"
                                                                        id="label-address-state"
                                                                        value={addressState.state?.selection}
                                                                        ref={register({ required: true, pattern: /^[a-zA-Z.\s]+$/ })}
                                                                        onChange={(e) => {
                                                                            setAddressState({
                                                                                ...addressState,
                                                                                state: { ...addressState.state, selection: e.target.value },
                                                                            });
                                                                            clearErrors("billingAddress");
                                                                        }}
                                                                        aria-required="true"
                                                                    >
                                                                        {CAProvinces.map((val: any) => (
                                                                            <option key={val.name} value={val.value}>
                                                                                {val.name}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    {errors["state"] && (errors["state"].type === "required" || errors["state"].type === "pattern") && (
                                                                        <span className="form-error">
                                                                            {tLang("manageAccount.protectionPlan.billingInformation.validation.province.required")}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                        ) : (
                                            <div className="row">
                                                <div className="col-xs-12 col-sm-4 col-no-gutter">
                                                    <legend className="legend-label">
                                                        {" "}
                                                        <label htmlFor="label-address-state">
                                                            {tLang("manageAccount.protectionPlan.billingInformation.label.state")}{" "}
                                                            <span className="required" title="Mandatory">
                                                                *
                                                            </span>
                                                        </label>
                                                    </legend>
                                                </div>

                                                <div className="col-xs-12 col-sm-8 col-no-gutter">
                                                    <div className="row">
                                                        <div className="col-xs-12 col-no-gutter">
                                                            <div className={`form-row row ${errors["state"] && "status-error"}`}>
                                                                <div className="form-input col-xs-12 col-no-gutter">
                                                                    <select
                                                                        name="state"
                                                                        className="inputbox"
                                                                        id="label-address-state"
                                                                        value={addressState.state?.selection}
                                                                        ref={register({ required: true, pattern: /^[a-zA-Z.\s]+$/ })}
                                                                        onChange={(e) => {
                                                                            setAddressState({
                                                                                ...addressState,
                                                                                state: { ...addressState.state, selection: e.target.value },
                                                                            });
                                                                            clearErrors("billingAddress");
                                                                        }}
                                                                        aria-required="true"
                                                                    >
                                                                        {stateList.map((val: any) => (
                                                                            <option key={val.name} value={val.value}>
                                                                                {val.name}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    {errors["state"] && (errors["state"].type === "required" || errors["state"].type === "pattern") && (
                                                                        <span className="form-error">
                                                                            {tLang("manageAccount.protectionPlan.billingInformation.validation.state.required")}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                    </fieldset>
                                    <fieldset className="form-row inline">
                                        {accountInfo?.country !== null && accountInfo?.country === "CA" ? (
                                            <div className="row">
                                                <div className="col-xs-12 col-sm-4 col-no-gutter">
                                                    <legend className="legend-label">
                                                        {" "}
                                                        <label htmlFor="label-address-zipcode">
                                                            Postal Code{" "}
                                                            <span className="required" title="Mandatory">
                                                                *
                                                            </span>
                                                        </label>
                                                    </legend>
                                                </div>

                                                <div className="col-xs-12 col-sm-8 col-no-gutter">
                                                    <div className="row">
                                                        <div className="col-xs-12 col-no-gutter">
                                                            <div className={`form-row row ${errors["zipcode"] && "status-error"}`}>
                                                                <div className="form-input col-xs-12 col-no-gutter">
                                                                    <input
                                                                        type="text"
                                                                        name="zipcode"
                                                                        className="inputbox"
                                                                        onChange={(e) => {
                                                                            setAddressState({ ...addressState, zipCode: e.target.value });
                                                                            clearErrors("billingAddress");
                                                                        }}
                                                                        defaultValue={addressState.zipCode}
                                                                        id="label-address-zipcode"
                                                                        ref={register({ required: true, pattern: PATTERNS.POSTALCODE.pattern })}
                                                                        placeholder={tLang("manageAccount.protectionPlan.billingInformation.placeholder.postalCode")}
                                                                        aria-required="true"
                                                                    />
                                                                    {errors["zipcode"] && errors["zipcode"].type === "required" && (
                                                                        <span className="form-error">
                                                                            {tLang("manageAccount.protectionPlan.billingInformation.validation.postalCode.required")}
                                                                        </span>
                                                                    )}
                                                                    {errors["zipcode"] && errors["zipcode"].type === "pattern" && (
                                                                        <span className="form-error">
                                                                            "Please Enter a Valid Postal Code"
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="row">
                                                <div className="col-xs-12 col-sm-4 col-no-gutter">
                                                    <legend className="legend-label">
                                                        {" "}
                                                        <label htmlFor="label-address-zipcode">
                                                            {zipCode.Label}{" "}
                                                            <span className="required" title="Mandatory">
                                                                *
                                                            </span>
                                                        </label>
                                                    </legend>
                                                </div>

                                                <div className="col-xs-12 col-sm-8 col-no-gutter">
                                                    <div className="row">
                                                        <div className="col-xs-12 col-no-gutter">
                                                            <div className={`form-row row ${errors["zipcode"] && "status-error"}`}>
                                                                <div className="form-input col-xs-12 col-no-gutter">
                                                                    <input
                                                                        type="text"
                                                                        name="zipcode"
                                                                        className="inputbox"
                                                                        onChange={(e) => {
                                                                            setAddressState({ ...addressState, zipCode: e.target.value });
                                                                            clearErrors("billingAddress");
                                                                        }}
                                                                        defaultValue={addressState.zipCode}
                                                                        id="label-address-zipcode"
                                                                        ref={register({ required: true, pattern: PATTERNS.ZIPCODE.pattern })}
                                                                        placeholder={tLang("manageAccount.protectionPlan.billingInformation.placeholder.zipCode")}
                                                                        aria-required="true"
                                                                    />
                                                                    {errors["zipcode"] && errors["zipcode"].type === "required" && (
                                                                        <span className="form-error">
                                                                            {tLang("manageAccount.protectionPlan.billingInformation.validation.zipCode.required")}
                                                                        </span>
                                                                    )}
                                                                    {errors["zipcode"] && errors["zipcode"].type === "pattern" && (
                                                                        <span className="form-error">
                                                                            {tLang("manageAccount.protectionPlan.billingInformation.validation.zipCode.pattern")}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </fieldset>
                                    <fieldset className="form-row inline">
                                        <div className="row">
                                            <div className="col-xs-12 col-sm-4 col-no-gutter">
                                                <legend className="legend-label">
                                                    <label htmlFor={"label-address-phone-number"}>
                                                        {tLang("manageAccount.protectionPlan.billingInformation.label.phoneNumber")}{" "}
                                                        <span className="required" title="Mandatory">
                                                            *
                                                        </span>
                                                    </label>
                                                </legend>
                                            </div>

                                            <div className="col-xs-12 col-sm-8 col-no-gutter">
                                                <div className="row">
                                                    <div className="col-xs-12 col-no-gutter">
                                                        <div className={`form-row row ${errors["phoneNumber"] && "status-error"}`}>
                                                            <div className="form-input col-xs-12 col-no-gutter">
                                                                {addressState && (
                                                                    <>
                                                                        <Controller
                                                                            name={`phoneNumber`}
                                                                            control={control}
                                                                            onFocus={() => {
                                                                                if (phoneInputRef?.current) {
                                                                                    phoneInputRef.current?.focus();
                                                                                }
                                                                            }}
                                                                            defaultValue={addressState.phone ? addressState.phone : ""}
                                                                            className="inputbox"
                                                                            rules={{
                                                                                required: true,
                                                                                validate: (value) =>
                                                                                    value
                                                                                        ? isValidPhoneNo(
                                                                                            accountInfo && accountInfo.country ? accountInfo.country : "US",
                                                                                            value
                                                                                        )
                                                                                        : false,
                                                                            }}
                                                                            onChange={(e: any) => {
                                                                                updatePhoneNumber(e);
                                                                                clearErrors("billingAddress");
                                                                            }}
                                                                            render={({ onChange, value, ref }) => {
                                                                                phoneInputRef = ref;

                                                                                return (
                                                                                    <PhoneInput
                                                                                        ref={ref}
                                                                                        value={value}
                                                                                        onChange={onChange}
                                                                                        international={false}
                                                                                        addInternationalOption={false}
                                                                                        className="inputbox"
                                                                                        defaultCountry={accountInfo && accountInfo.country ? accountInfo.country : "US"}
                                                                                        countries={[accountInfo && accountInfo.country ? accountInfo.country : "US"]}
                                                                                        id={"label-address-phone-number"}
                                                                                        placeholder={tLang(
                                                                                            "manageAccount.protectionPlan.billingInformation.placeholder.phoneNumber"
                                                                                        )}
                                                                                        maxLength={15}
                                                                                        minLength={10}
                                                                                        aria-required="true"
                                                                                    />
                                                                                );
                                                                            }}
                                                                        />
                                                                        {errors["phoneNumber"] && errors["phoneNumber"].type === "required" && (
                                                                            <span className="form-error">
                                                                                {tLang("manageAccount.protectionPlan.billingInformation.validation.phone.required")}
                                                                            </span>
                                                                        )}
                                                                        {errors["phoneNumber"] && errors["phoneNumber"].type === "validate" && (
                                                                            <span className="form-error">
                                                                                {tLang("manageAccount.protectionPlan.billingInformation.validation.phone.validate")}
                                                                            </span>
                                                                        )}
                                                                        {errors["phoneNumber"] && errors["phoneNumber"].type === "minLength" && (
                                                                            <span className="form-error">
                                                                                {tLang("manageAccount.protectionPlan.billingInformation.validation.phone.minLength")}
                                                                            </span>
                                                                        )}
                                                                        {errors["phoneNumber"] && errors["phoneNumber"].type === "maxLength" && (
                                                                            <span className="form-error">
                                                                                {tLang("manageAccount.protectionPlan.billingInformation.validation.phone.minLength")}
                                                                            </span>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </fieldset>
                                    {errors["billingAddress"] && (
                                        <fieldset className="form-row inline">
                                            <div className="row">
                                                <div className="col-xs-12 col-sm-4 col-no-gutter"></div>
                                                <div className="col-xs-12 col-sm-8 col-no-gutter">
                                                    <span className="form-error">{tLang("manageAccount.protectionPlan.billingInformation.validation.invalidAddress")}</span>
                                                </div>
                                            </div>
                                        </fieldset>
                                    )}
                                </React.Fragment>
                            </div>
                        </>
                    )}
                </div>
                <div className="dialog-footer">
                    <div className="row between-xs">
                        <div className="col-xs col-no-gutter flex-grow-null">
                            <button
                                type="button"
                                className="btn-default btn-link btn-no-padding btn-no-border"
                                id="btn-cancel-protection-plan-billing"
                                onClick={() => {
                                    window.addToDataLayer({ event: "Protection Plan - Close billing info" });
                                    RefreshAndClose();
                                }}
                            >
                                <span className="btn-text">Cancel</span>
                            </button>
                        </div>
                        <div className="col-xs col-no-gutter flex-grow-null">
                            <button
                                type="submit"
                                className="btn-default btn-primary"
                                id="btn-submit-update-billing"
                                disabled={loading}
                                onClick={() => window.addToDataLayer({ event: "Protection Plan - Submit Billing Info" })}
                            >
                                <span className="btn-text">{billingOperation()} Billing</span>
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </React.Fragment>
    );
};
export default IDFProtectionPlanBillingInfo;
